import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormControl, FormHelperText, InputLabel } from '@mui/material';
import { withStyles } from '@mui/styles';
import { DatePicker } from '@mui/x-date-pickers';
import { compose } from 'redux';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { FORMAT_BY_LOCALE, getCurrentDateFormat } from '../../utils';
import FormControlForDataRangePicker from './FormControlForDataRangePicker';

const styles = theme => ({
    datePickerRight: {
        color: theme.palette.error.main,
    },
    datePickerLeft: {
        color: theme.palette.error.main,
        marginLeft: 0
    }
});
const DateRangePicker = props => {
    const {
        isColumn = false,
        startName,
        endName,
        title,
        minDate,
        maxDate,
        value,
        errors,
        handleChangeStartDate,
        handleChangeEndDate,
        handleError,
        classes,
        t
    } = props;

    const { i18n } = useTranslation();

    useEffect(() => {
        dayjs.locale(i18n.language);
    }, [i18n.language]);

    return (
        <>
            <InputLabel
                shrink
                variant="standard"
                style={{ marginTop: '15px' }}
            >
                {t(title)}
            </InputLabel>
            <FormControlForDataRangePicker
                fullWidth
                margin="dense"
                isColumn={isColumn}
            >
                <FormControl sx={{ width: isColumn ? '100%' : '50%' }}>
                    <DatePicker
                        format={FORMAT_BY_LOCALE[i18n.language]}
                        label={t('startDate')}
                        value={value.startDate}
                        minDate={minDate}
                        maxDate={maxDate}
                        onChange={e => handleChangeStartDate(startName)(e)}
                        onError={error => handleError(error, startName)}
                        slotProps={{ textField: { variant: 'standard' },
                            field: { clearable: true,
                                sx: {
                                    paddingRight: '10px'
                                } },
                            inputAdornment: {
                                position: 'start',
                                sx: {
                                    paddingLeft: '10px'
                                }
                            },
                            openPickerButton: {
                                sx: theme => ({
                                    color: errors[startName] ? theme.palette.error.main : 'inherit',
                                })
                            }, }}
                    />
                    {!!errors[startName] && (
                        <FormHelperText className={classes.datePickerLeft}>
                            {t(errors[startName], {
                                maxDate: maxDate.format(getCurrentDateFormat()),
                                minDate: minDate.format(getCurrentDateFormat()),
                            })}
                        </FormHelperText>
                    )}
                </FormControl>

                <FormControl
                    sx={{ marginTop: isColumn ? '10px' : 0,
                        width: isColumn ? '100%' : '50%' }}
                >
                    <DatePicker
                        className={classes.datePickerRight}
                        format={FORMAT_BY_LOCALE[i18n.language]}
                        label={t('endDate')}
                        value={value.endDate}
                        minDate={value.startDate || minDate}
                        maxDate={maxDate}
                        onChange={e => handleChangeEndDate(endName)(e)}
                        onError={error => handleError(error, endName)}
                        slotProps={{ textField: { variant: 'standard', },
                            field: { clearable: true,
                                sx: {
                                    paddingRight: '10px'
                                } },
                            inputAdornment: {
                                position: 'start',
                                sx: {
                                    paddingLeft: '10px'
                                }
                            },
                            openPickerButton: {
                                sx: theme => ({
                                    color: errors[endName] ? theme.palette.error.main : 'inherit',
                                    marginRight: 0
                                })
                            }, }}
                        sx={{ width: '50%' }}
                    />
                    {!!errors[endName] && (
                        <FormHelperText className={classes.datePickerRight}>
                            {t(errors[endName], {
                                maxDate: maxDate.format(getCurrentDateFormat()),
                                minDate: value.startDate ?
                                    value.startDate.format(getCurrentDateFormat()) :
                                    minDate.format(getCurrentDateFormat())
                            })}
                        </FormHelperText>
                    )}
                </FormControl>
            </FormControlForDataRangePicker>
        </>
    );
};

DateRangePicker.propTypes = {
    startName: PropTypes.string.isRequired,
    endName: PropTypes.string.isRequired,
    title: PropTypes.string,
    handleChangeStartDate: PropTypes.func.isRequired,
    handleChangeEndDate: PropTypes.func.isRequired,
    handleError: PropTypes.func,
    minDate: PropTypes.object,
    value: PropTypes.object,
    maxDate: PropTypes.object,
    errors: PropTypes.object,
    classes: PropTypes.object,
};

export default compose(
    withStyles(styles)
)(DateRangePicker);
